import { useEffect, useState } from "react";
import MonitorVista from "../vista/monitorVista";
import { estadoInicialFiltroMonitor } from "../interfaces";
import type { IFiltroMonitor, IEntidadesRegistroMonitor, IMonitorMarker } from "../interfaces";
import ModeloUsuario from "../../../modelos/Usuario";
import MonitorModelo from "../../../modelos/Monitor";
import { useNotificaciones } from "../../../globales/contextos/notificationContext";
import { useCargando } from "../../../globales/contextos/cargandoContext";
import { EnumTipoNotificacion } from "../../../globales/interfaces";
import { EnumPerfiles } from "../../../globales/enums/enumPerfiles";
import { estadoInicialFiltroUsuario } from "../../usuarios/interfaces";
import { estadoInicialFiltroTarea } from "../../Tareas/interfaces";
import type { ITrazabilidadTecnico } from "../../../modelos/Monitor/entidades/ITrazabilidadTecnico";
import dayjs from "dayjs";
import { ProyectoModelo } from "../../../modelos/Proyectos";
import { TareaModelo } from "../../../modelos/Tarea";
import type { IProyecto } from "../../../modelos/Proyectos/entidades/IProyecto";

const MonitorCV = () => {
    const modeloUsuario = new ModeloUsuario();
    const modeloMonitor = new MonitorModelo();
    const modeloProyecto = new ProyectoModelo();
    const modeloTarea = new TareaModelo();

    const { mostrarNotificacion } = useNotificaciones();
    const { manejarCargando } = useCargando();

    const [filtro, setFiltro] = useState<IFiltroMonitor>(estadoInicialFiltroMonitor);
    const [entidadesRegistro, setEntidadesRegistro] = useState<IEntidadesRegistroMonitor>({
        Tecnicos: []
    });
    const [markers, setMarkers] = useState<IMonitorMarker[]>([]);
    const [proyectos, setProyectos] = useState<IProyecto[]>([]);
    const [cargando, setCargando] = useState<boolean>(false);

    useEffect(() => {
        cargarTecnicos();
        cargarProyectos();
    }, []);

    const cargarTecnicos = async () => {
        try {
            manejarCargando(true, "Cargando técnicos...");
            const respuesta = await modeloUsuario.obtenerListaUsuarios(estadoInicialFiltroUsuario);
            const tecnicos = respuesta.Data.filter(u => u.PerfilUsuarioId === EnumPerfiles.Tecnico && u.Activo && !u.Eliminado);
            setEntidadesRegistro({
                Tecnicos: tecnicos
            });
        } catch (error) {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                "Error al cargar técnicos",
                "No fue posible obtener la lista de técnicos para el filtro.",
                5000
            );
        } finally {
            manejarCargando(false);
        }
    };

    const cargarProyectos = async () => {
        try {
            const respuesta = await modeloProyecto.obtenerListaProyectos("");
            const proyectosActivos = (respuesta.Data || []).filter(p => !p.Eliminado && p.Activo);
            setProyectos(proyectosActivos);
        } catch (error) {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                "Error al cargar proyectos",
                "No fue posible obtener la lista de proyectos para el mapa.",
                5000
            );
        }
    };

    const manejarCambioFiltroMonitor = (campo: keyof IFiltroMonitor, valor: any) => {
        setFiltro(prev => ({
            ...prev,
            [campo]: valor
        }));
    };

    const limpiarFiltros = () => {
        setFiltro(estadoInicialFiltroMonitor);
        setMarkers([]);
    };

    const consultarTrazabilidad = async () => {
        if (!filtro.UsuarioId) {
            mostrarNotificacion(
                EnumTipoNotificacion.Warning,
                "Campo requerido",
                "Por favor seleccione un técnico para consultar.",
                3000
            );
            return;
        }

        try {
            setCargando(true);
            manejarCargando(true, "Consultando trazabilidad...");

            const tecnicoSeleccionado = entidadesRegistro.Tecnicos.find(t => t.Id === filtro.UsuarioId);
            const nombreTecnico = tecnicoSeleccionado ? tecnicoSeleccionado.NombreCompleto : "Técnico";

            // Llamada real al endpoint del backend
            const respuesta = await modeloMonitor.obtenerTrazabilidadTecnico(filtro.UsuarioId, filtro.Fecha);
            let datosTrazabilidad: ITrazabilidadTecnico[] = respuesta.Data || [];

            // Caso de Prueba Mock (Luis): Si se consulta por Luis
            const esLuis = nombreTecnico.toLowerCase().includes("luis");
            if (esLuis && datosTrazabilidad.length === 0) {
                datosTrazabilidad = [
                    {
                        ServicioId: 101,
                        Tecnico: nombreTecnico,
                        Orden: 1,
                        TipoPunto: "Ingreso / Inicio de Jornada",
                        Fecha: filtro.Fecha ? dayjs(filtro.Fecha).hour(8).minute(0).toISOString() : new Date().toISOString(),
                        Ubicacion: "4.7110,-74.0721"
                    },
                    {
                        ServicioId: 102,
                        Tecnico: nombreTecnico,
                        Orden: 2,
                        TipoPunto: "Llegada a Punto de Tarea",
                        Fecha: filtro.Fecha ? dayjs(filtro.Fecha).hour(9).minute(30).toISOString() : new Date(Date.now() + 30 * 60 * 1000).toISOString(),
                        Ubicacion: "4.7150,-74.0680"
                    },
                    {
                        ServicioId: 103,
                        Tecnico: nombreTecnico,
                        Orden: 3,
                        TipoPunto: "Inicio de Actividad",
                        Fecha: filtro.Fecha ? dayjs(filtro.Fecha).hour(10).minute(15).toISOString() : new Date(Date.now() + 45 * 60 * 1000).toISOString(),
                        Ubicacion: "4.7200,-74.0750"
                    }
                ];
            }

            if (datosTrazabilidad.length === 0) {
                setMarkers([]);
                mostrarNotificacion(
                    EnumTipoNotificacion.Warning,
                    "Sin resultados",
                    `No se encontró trazabilidad registrada para el técnico ${nombreTecnico} en la fecha seleccionada.`,
                    4000
                );
                return;
            }

            // Helper para validar si la ubicación cuenta con coordenadas GPS válidas (distintas de 0,0)
            const tieneUbicacionValida = (ubicacion?: string): boolean => {
                if (!ubicacion) return false;
                const partes = ubicacion.split(",");
                if (partes.length < 2) return false;
                const lat = parseFloat(partes[0].trim());
                const lng = parseFloat(partes[1].trim());
                return !isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0);
            };

            // Helper para identificar inicio de sesión
            const esEventoInicioSesion = (tipoPunto?: string) => {
                const str = (tipoPunto || "").toLowerCase().trim();
                return str === "login" || str === "ingresojornada" || str === "ingreso" || str.includes("sesión") || str.includes("jornada");
            };

            // 1. Filtrar únicamente los registros que tengan ubicación GPS válida
            const datosConUbicacionValida = datosTrazabilidad.filter(item => tieneUbicacionValida(item.Ubicacion));

            if (datosConUbicacionValida.length === 0) {
                setMarkers([]);
                mostrarNotificacion(
                    EnumTipoNotificacion.Warning,
                    "Sin ubicación GPS",
                    `El técnico ${nombreTecnico} tiene registros pero ninguno cuenta con ubicación GPS válida.`,
                    4000
                );
                return;
            }

            // 2. Deduplicar eventos de inicio de sesión si vienen múltiples por cada servicio
            const trazabilidadFiltrada: ITrazabilidadTecnico[] = [];
            const loginsVistos = new Set<string>();

            datosConUbicacionValida.forEach(item => {
                if (esEventoInicioSesion(item.TipoPunto)) {
                    // Generar clave única para el inicio de sesión basado en fecha/ubicación
                    const fechaClave = item.Fecha ? dayjs(item.Fecha).format("YYYY-MM-DD HH:mm") : "sin-fecha";
                    const claveLogin = `${item.Ubicacion || ''}_${fechaClave}`;
                    if (!loginsVistos.has(claveLogin)) {
                        loginsVistos.add(claveLogin);
                        trazabilidadFiltrada.push(item);
                    }
                } else {
                    trazabilidadFiltrada.push(item);
                }
            });

            // 3. Ordenar por fecha descendente para obtener el punto válido más reciente (último punto según fecha y hora)
            trazabilidadFiltrada.sort((a, b) => {
                const fechaA = a.Fecha ? dayjs(a.Fecha).valueOf() : 0;
                const fechaB = b.Fecha ? dayjs(b.Fecha).valueOf() : 0;
                if (fechaB !== fechaA) return fechaB - fechaA;
                return b.Orden - a.Orden;
            });

            // 4. Filtrar para mostrar ÚNICAMENTE el último punto con ubicación válida
            const ultimoPunto = trazabilidadFiltrada.slice(0, 1);

            // Procesar y mapear a marcadores
            const nuevosMarcadores: IMonitorMarker[] = [];
            for (let index = 0; index < ultimoPunto.length; index++) {
                const item = ultimoPunto[index];
                if (item.Ubicacion) {
                    const partes = item.Ubicacion.split(",");
                    if (partes.length >= 2) {
                        const lat = parseFloat(partes[0].trim());
                        const lng = parseFloat(partes[1].trim());
                        if (!isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0)) {
                            const esLogin = esEventoInicioSesion(item.TipoPunto);
                            let estadoTareaId: number | undefined = undefined;
                            let estadoTareaNombre: string | undefined = undefined;

                            if (!esLogin && item.ServicioId && item.ServicioId > 0) {
                                try {
                                    const resTarea = await modeloTarea.obtenerListaTareas({
                                        ...estadoInicialFiltroTarea,
                                        TareaId: item.ServicioId
                                    });
                                    const tareaObj = (resTarea.Data || [])[0];
                                    if (tareaObj) {
                                        estadoTareaId = tareaObj.EstadoTareaId;
                                        estadoTareaNombre = tareaObj.EstadoServicio?.Nombre;
                                    }
                                } catch (e) {
                                    console.error("No se pudo obtener el estado de la tarea", e);
                                }
                            }

                            nuevosMarcadores.push({
                                id: `${item.ServicioId}-${item.Orden}-${index}`,
                                latitud: lat,
                                longitud: lng,
                                orden: item.Orden,
                                tipoPunto: item.TipoPunto,
                                fecha: item.Fecha,
                                tecnico: item.Tecnico || nombreTecnico,
                                titulo: esLogin ? "Inicio de Sesión" : `Tarea #${item.ServicioId}`,
                                esInicioSesion: esLogin,
                                servicioId: item.ServicioId,
                                estadoTareaId,
                                estadoTareaNombre
                            });
                        }
                    }
                }
            }

            setMarkers(nuevosMarcadores);

            mostrarNotificacion(
                EnumTipoNotificacion.Success,
                "Consulta exitosa",
                `Se cargó el último punto registrado para el técnico ${nombreTecnico}.`,
                4000
            );

        } catch (error) {
            mostrarNotificacion(
                EnumTipoNotificacion.Error,
                "Error en la consulta",
                "Ocurrió un error al obtener los datos de trazabilidad desde el servidor.",
                5000
            );
        } finally {
            setCargando(false);
            manejarCargando(false);
        }
    };

    return (
        <MonitorVista
            filtro={filtro}
            consultarTrazabilidad={consultarTrazabilidad}
            entidadesRegistro={entidadesRegistro}
            manejarCambioFiltroMonitor={manejarCambioFiltroMonitor}
            limpiarFiltros={limpiarFiltros}
            markers={markers}
            cargando={cargando}
            proyectos={proyectos}
        />
    );
};

export default MonitorCV;
